import { useMutation } from '@tanstack/react-query';

import { SignInParameters } from '../../../../../typings/api/skymap/rest/v0/auth';
import { DetermineAuthFlowParameters } from '../../../../../typings/api/skymap/rest/v1/auth';
import { AuthService } from '../../../services/axios/services/v1/auth-service';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../utils/typescript-utils';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { Form } from './sign-in-form';

export type DetermineAuthFlowResponse = Awaited<ReturnType<AuthService['determineAuthFlow']>>;
export type SignInResponse = Awaited<ReturnType<AuthService['signIn']>>;

type Props = {
  onSuccessDetermineFlow: (response: DetermineAuthFlowResponse) => void | Promise<void>;
  onSuccessSignIn: (response: SignInResponse) => void | Promise<void>;
};

const useAuthenticationFlowQuery = (props: Props) => {
  const {
    handleError,
    hasError,
    clearErrors,
    getErrorForPath,
    buildErrorList,
    excludeErrorPaths,
    translatedErrorMessages,
  } = useErrorHandling<
    RequestParamsToApiPaths<DetermineAuthFlowParameters> | RequestParamsToApiPaths<SignInParameters>
  >();

  // "determineAuthFlow" mutation.
  const determineAuthFlowMutation = useMutation({
    mutationFn: (email: string) =>
      SkyMapAxiosServiceFactory.instance.createAuthServiceV1().determineAuthFlow({
        query: { email },
      }),
    networkMode: 'always',
    retry: false,
    onMutate: () => clearErrors(),
    onError: (error) => handleError(error),
    onSuccess: (response) => props.onSuccessDetermineFlow(response),
  });

  // "signIn" mutation.
  const signInMutation = useMutation({
    mutationFn: (form: Form) => {
      return SkyMapAxiosServiceFactory.instance.createAuthServiceV1().signIn({
        body: {
          email: form.email,
          password: form.password,
        },
      });
    },
    networkMode: 'always',
    retry: false,
    onMutate: () => clearErrors(),
    onError: (error) => handleError(error),
    onSuccess: (response) => props.onSuccessSignIn(response),
  });

  return {
    // Separate mutations.
    determineAuthFlowMutation,
    signInMutation,

    // Shared error-handling.
    hasError,
    buildErrorList,
    getErrorForPath,
    excludeErrorPaths,
    clearErrors,
    translatedErrorMessages,
  };
};

export { useAuthenticationFlowQuery };
